import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Paper,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import { AccessToken, Pricelist } from 'common-models';

import { ChangeEvent, FC, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStorePricelistMutation } from '../../features/events/eventsApiSlice';
import { Model } from '../../lib/Model';
import PhotoProductPriceEditor from './PhotoProductPriceEditor';


const PricelistEditor: FC<{
    Pricelist: Pricelist,
    accessToken: AccessToken,
    onSavedCallback: () => void,
    onCanceledCallback: () => void
}> = ({ Pricelist, accessToken, onSavedCallback, onCanceledCallback }) => {
    console.info(`Pricelist: ${JSON.stringify(Pricelist)}`);

    const [storePricelist] = useStorePricelistMutation();

    const [localPricelist, setLocalPricelist] = useState(Model.deepCopyPricelist(Pricelist));


    const [isSavingPricelist, setIsSavingPricelist] = useState(false);

    const addProduct = () => {
        localPricelist.products.push({
            productId: uuidv4(),
            names: {
                sv: '',
                en: ''
            },
            priceListId: localPricelist.id,
            prices: []
        })
        setLocalPricelist({
            id: localPricelist.id,
            name: localPricelist.name,
            products: localPricelist.products
        });
    }


    useEffect(() => {
        console.info(`localPricelist: ${JSON.stringify(localPricelist)}`);
    }, [localPricelist])


    const onSavePricelistClick = async () => {
        setIsSavingPricelist(true);
        try {
            await storePricelist([localPricelist, accessToken]);
        } catch (error) {
            console.info(error);
        }
        setIsSavingPricelist(false);
        onSavedCallback();
    }

    const onCancelButtonClick = async () => {
        onCanceledCallback();
    }

    const onChangePricelistName = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        localPricelist.name = (event.target as HTMLInputElement).value;
    }


    const theme = useTheme();

    return (
        <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }}>
            {localPricelist !== undefined ? (
                <Box component={Paper} elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
                    {/* Header */}
                    <Box sx={{
                        p: 3,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText
                    }}>
                        <Typography variant="h4" component="h1">
                            Edit Pricelist
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                            Configure product pricing and details
                        </Typography>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        {/* Basic Details Section */}
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardHeader
                                title={
                                    <Typography variant="h6" color="text.primary">
                                        Basic Details
                                    </Typography>
                                }
                                sx={{ pb: 1 }}
                            />
                            <Divider />
                            <CardContent>
                                <TextField
                                    fullWidth
                                    defaultValue={localPricelist.name}
                                    variant="outlined"
                                    label="Pricelist Name"
                                    onChange={onChangePricelistName}
                                    placeholder="Enter a descriptive name for this pricelist"
                                    sx={{ maxWidth: 400 }}
                                />
                            </CardContent>
                        </Card>

                        {/* Products Section */}
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardHeader
                                title={
                                    <Typography variant="h6" color="text.primary">
                                        Products & Pricing
                                    </Typography>
                                }
                                action={
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addProduct}
                                        size="small"
                                    >
                                        Add Product
                                    </Button>
                                }
                                sx={{ pb: 1 }}
                            />
                            <Divider />
                            <CardContent sx={{ p: 0 }}>
                                {localPricelist.products.length > 0 ? (
                                    <Box sx={{ p: 2 }}>
                                        {localPricelist.products.map((product, index) => (
                                            <Box key={`priceeditor|${product.productId}`} sx={{ mb: 2 }}>
                                                {index > 0 && <Divider sx={{ mb: 2 }} />}
                                                <PhotoProductPriceEditor photoProduct={product} />
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No products added yet. Click "Add Product" to get started.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Action Buttons */}
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                            pt: 2,
                            borderTop: `1px solid ${theme.palette.divider}`
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                disabled={isSavingPricelist}
                                onClick={onCancelButtonClick}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                disabled={isSavingPricelist}
                                onClick={onSavePricelistClick}
                            >
                                {isSavingPricelist ? 'Saving...' : 'Save Pricelist'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 200
                }}>
                    <Typography variant="body1" color="text.secondary">
                        Loading pricelist...
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

export default PricelistEditor;
